import React, { useState, useEffect } from 'react';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import {useMutation, useLazyQuery, useQuery} from '@apollo/client';

import {useNavigate, useParams} from 'react-router-dom';

import './Users.create.scss';
import Button from '../../forms/buttons/Button';
import { PatientAvatarUpload } from '../../visuals/Patient.avatar.uploader';
import gqlSchemaLibrary from '../../services/graphql.schemas';
import { clearGqlTypename } from '../../utils/clearTypename';
import { userOptionsToRoles } from "./Users.create.roleSelect";
import CreateUserForm from './Users.create.form';
import FormValidation from "../../forms/validation/formValidation";
import {useTranslation} from "react-i18next";
import {PageViewLog} from "../../services/interactions/page.view";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";


const userCoreFields = {
    'bio.lastname': { type: 'text', required: true, value: ''},
    'bio.firstname': { type: 'text', required: true, value: '' },
    'email': { type: 'email',  required: true, value: '' },
    'userRoles': { type: 'object', value: null },
    'receiveEmail': { type: 'boolean',  required: false, value: true }
};

const clinicianFields = {
    'role': { type: 'text', value: ''},
    'dateOfBirth': { type: 'date', value: null},
    'phones': { type: 'array-phones', required: false, arrayRequired: -1, value: null },
    'address': { type: 'array-items', required: false,  arrayRequired: -1, value: null }
};

const fields = Object.assign({}, userCoreFields, clinicianFields);

function UserCreate({ event, currentUser, match }) {
    let { clinicianId } = useParams();
    const navigate = useNavigate();
    const [isEditting, setIsEditting] = useState(false);
    const [edittingIds, setEdittingIds] = useState({ userId: '', clinicianId: '' });
    const [user, setUser] = useState(null);
    const [currentOrg, setCurrentOrg] = useState(null);
    const [defaultCountry, setDefaultCountry] = useState(null);

    const [dataFields, setDataFields] = useState(fields);
    const [formValid, setFormValid] = useState(false);

    const [clinician, setClinician] = useState({});
    const [userCore, setUserCore] = useState({});

    // Helper UI
    const [emailSubject] = useState(new Subject())

    // Redirect to ClinicianList
    const [__submitting, __setSubmitting] = useState(false);

    // UI Element
    const parentRef = React.createRef();
    const [toTop, setToTop] = useState(false);

    const {t} = useTranslation(['users','common'])

    const manageScrollToBottom = () => {
        const scrollPos = parentRef.current.scrollTop;
        if(scrollPos > 80) {
                setToTop(true);
            } else {
                setToTop(false);
            }
    }

    useQuery(gqlSchemaLibrary.organizations.query.getCurrentOrg, {
        onCompleted: ({ getCurrentOrg }) => {
            console.log('getCurrentOrg: ', getCurrentOrg);
            if (getCurrentOrg && getCurrentOrg.defaultCountry) {
                setDefaultCountry(getCurrentOrg.defaultCountry);
            } else if(process.env.REACT_APP_DEFAULT_COUNTRY){
                setDefaultCountry(process.env.REACT_APP_DEFAULT_COUNTRY);
            } else {
                setDefaultCountry('us');
            }
            setCurrentOrg(getCurrentOrg);
        },
        fetchPolicy: 'cache-and-network'
    });

    const showErrors = (e) => {
        const errors = e.graphQLErrors;
        const emailError = errors.find((e) => e.extensions && e.extensions.length !== 0 && e.extensions[0] === "email");
        if (emailError) {
            let field = dataFields.email;
            field.error = true;
            field.errorMessage = emailError.message;
            setDataFields(prevState => ({
                ...prevState,
                ["email"]: field
            }));
        }
        console.error('Update Clinician Error: ', e);
        __setSubmitting(false);
    }

    const [createClinician] = useMutation(gqlSchemaLibrary.clinicians.mutations.createOrgUser, {
        onCompleted: ({ createOrgUser }) => {
            console.log('clinician created: ', createOrgUser);
            navigate(`/users`);
        },
        onError: showErrors
    });

    const [updateCurrentClinician] = useMutation(gqlSchemaLibrary.clinicians.mutations.editOrgUser, {
        onCompleted: ({ editOrgUser }) => {
            console.log('clinician updated: ', editOrgUser);
            navigate(`/users`);
        },
        onError: showErrors
    });

    useEffect(() => {
        let form;
        const onFormChange = (key, value, field, valid) => {
            if (userCoreFields.hasOwnProperty(key)) {
                let u = userCore;
                if (key.search('bio.') !== -1) {
                    let k = key.replace('bio.', '');
                    if (!u.hasOwnProperty('bio')) {
                        u['bio'] = {};
                    }
                    u['bio'][k] = value;
                }else{
                    u[key] = value;
                }
                setUserCore(u);
            }else if (clinicianFields.hasOwnProperty(key)) {
                let p = clinician;
                p[key] = value;
                setClinician(p);
            }
            setDataFields(prevState => ({
                ...prevState,
                [key]: field
            }));
            setFormValid(valid);
        };

        if (clinicianId && !!user) {
            if (!isEditting || !user) {
                return;
            }
            // Actual clinician data
            const { dateOfBirth, role, clinic, _id, address, phones } = user;
            // Actual User core data
            const { _id: _userId, email, bio, receiveEmail, userRoles } = user.user;

            form = FormValidation(fields, onFormChange);

            // clinician
            if (dateOfBirth) {
                form.setValue('dateOfBirth', dateOfBirth);
            }
            form.setValue('role', role);

            let _address = !!address && Array.isArray(address) ? address : [];
            for (let i2 = 0; i2 < _address.length; i2++) {
                let type = _address[i2].type;
                if (_address[i2].type === 'Office') {
                    // The Office value was changed to Work
                    type = 'Work';
                } else if (_address[i2].type === 'Clinic') {
                    // the clinic value is no longer available
                    type = 'Home';
                }
                _address[i2].type = type;
            }
            if (_address.length !== 0) {
                form.setValue('address', _address);
            }

            let _phones = !!phones && Array.isArray(phones) ? phones : [];
            for (let i = 0; i < _phones.length; i++) {
                let label = _phones[i].label;
                if (_phones[i].label === t('office')) {
                    // The Office value was changed to Work
                    label = t('work');
                } else if (_phones[i].label === t('clinic')) {
                    // the clinic value is no longer available
                    label = t('mobile');
                }
                _phones[i].label = label;
            }
            if (_phones.length !== 0) {
                form.setValue('phones', _phones);
            }

            // userCore
            form.setValue('bio.lastname', !!bio && bio.lastname ? bio.lastname : '');
            form.setValue('bio.firstname', !!bio && bio.firstname ? bio.firstname : '');
            form.setValue('email', email || '');
            form.setValue('receiveEmail', receiveEmail || true);
            form.setValue('userRoles', userRoles);
            setDataFields(form.data);
        } else {
            form = FormValidation(fields, onFormChange);
            const defaultRole = userOptionsToRoles('clinician');
            form.setValue('userRoles', defaultRole.roles);
            form.setValue('role', defaultRole.option);
            form.setValue('receiveEmail', true);
            setDataFields(form.data);
        }

        if (!currentUser.userRoles.userClinicians.create || !currentUser.userRoles.userClinicians.edit) {
            navigate(-1);
        }
    }, [fields, user]);

    const [getClinicianToEdit] = useLazyQuery(gqlSchemaLibrary.clinicians.queries.getClinician, {
        onCompleted: ({ getClinician }) => {
            console.log('clinician to edit: ', getClinician);
            if (!getClinician.organizationIds.includes(currentUser.selectedOrgId)) {
                navigate(-1);
            }
            // Update current inputs to edit clinician, disable inputs where necessary
            setUser(clearGqlTypename(getClinician));
            setEdittingIds({
                userId: getClinician.user._id,
                clinicianId: getClinician._id
            })
        },
        fetchPolicy: 'cache-and-network'
    })

    // Check if this is an edit call
    useEffect(() => {
        if (clinicianId) {
            setIsEditting(true);
            getClinicianToEdit({
                variables: {
                    _id: clinicianId,
                    email: ''
                }
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [match]);

    // Email subscribe unsubscribe
    useEffect(() => {
        const subs = emailSubject.pipe(debounceTime(300)).subscribe((emailValue) => {
            console.log('Email Value: ', emailValue);
        });
        return () => {
            subs.unsubscribe();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const validateAndSubmit = () => {
        if (!currentUser.userRoles.userClinicians.edit && clinicianId) {
            return;
        } else if (!currentUser.userRoles.userClinicians.create) {
            return;
        }

        if (!formValid || __submitting) {
            return; // Not valid yet or submitting
        }

        console.log('User: ', userCore);
        console.log('Clinician: ', clinician);

        __setSubmitting(true);
        if (isEditting || clinicianId) {
            if (!edittingIds || !(edittingIds.userId && edittingIds.clinicianId)) {
                console.error('No Editting IDS exist!');
                __setSubmitting(false);
                return;
            }
            userCore['_id'] = edittingIds.userId;
            clinician['_id'] = edittingIds.clinicianId;
            updateCurrentClinician({
                variables: {
                    user: userCore,
                    clinician: clinician
                }
            })
        } else {
            createClinician({
                variables: {
                    user: userCore,
                    clinician: clinician
                }
            })
        }
    }

    const cancelUser = () => {
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button cancel form user'});
        navigate(-1);
    }

    const renderNextButton = () => {
        return (
            <Button disabled={!formValid} className="primary green smallPad" handleClick={validateAndSubmit}>
                <div className={`progressButton ${__submitting ? 'submitting' : ''}`}>
                    {__submitting ? <div className="_progressing"></div> : ''}
                    <span>{clinicianId ? t('done') : t('register')}</span>
                </div>
            </Button>
        )
    }

    return (
        <div id="UserCreate" className={`${toTop ? 'toTop' : ''}`}>
            <div className="headerTitle">
                <div className="imageUploadHolder">
                    <PatientAvatarUpload bio={dataFields['bio.lastname'].value ? dataFields['bio.lastname'].value : {}} hideIfNone={true} />
                </div>
                <div className="bioHolder">
                    <h2>{dataFields['bio.lastname'].value}{!!dataFields['bio.lastname'].value ? ',' : ''} {dataFields['bio.firstname'].value}</h2>
                    <p className="gender">
                    </p>
                </div>
                <Button className="tertiary white" handleClick={cancelUser}>
                    {t('common:cancel')}
                </Button>
                {renderNextButton()}
            </div>
            <div className="patientsections scrollBar" ref={parentRef} onScroll={manageScrollToBottom}>
                <div className="subSections">
                </div>
                <CreateUserForm event={event} dataFields={dataFields} defaultCountry={defaultCountry} />
            </div>
        </div>
    )
}

export default PageViewLog(UserCreate);
